<template>
  <div class="calendar-page">
    <div class="page-header">
      <button class="back-btn" @click="$router.back()">‹ 返回</button>
      <div class="page-title">选择出行日期</div>
    </div>

    <div class="calendar-wrap">
      <div class="calendar-month-nav">
        <button class="month-btn" @click="prevMonth">‹</button>
        <div class="month-label">{{ calYear }}年{{ calMonth + 1 }}月</div>
        <button class="month-btn" @click="nextMonth">›</button>
      </div>
      <div class="calendar-weekdays">
        <div v-for="d in ['日','一','二','三','四','五','六']" :key="d" class="weekday">{{ d }}</div>
      </div>
      <div class="calendar-days">
        <div
          v-for="(day, idx) in calDays"
          :key="idx"
          class="cal-day"
          :class="{
            'other-month': !day.current,
            'disabled': day.disabled,
            'selected': day.dateStr === selectedDateStr,
            'has-price': day.hasPrice
          }"
          @click="selectCalDate(day)"
        >
          <div class="cal-day-num">{{ day.date }}</div>
          <div v-if="day.hasPrice" class="cal-day-price">¥{{ day.minPrice }}</div>
        </div>
      </div>
      <div class="calendar-tip">请选择出行日期，绿色表示有价格</div>
    </div>

    <div v-if="selectedDateStr" class="price-detail">
      <div class="section-title">选择数量</div>
      <div class="price-list">
        <div v-for="item in priceItems" :key="item.id" class="price-item">
          <div class="price-info">
            <div class="price-name">{{ item.priceName }}</div>
            <div class="price-val">¥{{ item.price }}</div>
          </div>
          <div class="qty-ctrl">
            <button class="qty-btn" @click="changeQty(item, -1)">−</button>
            <span class="qty-num">{{ item.quantity || 0 }}</span>
            <button class="qty-btn" @click="changeQty(item, 1)">+</button>
          </div>
        </div>
      </div>

      <div class="price-total">
        <div class="total-label">合计</div>
        <div class="total-val">¥{{ totalPriceYuan }}</div>
      </div>
    </div>

    <div class="calendar-footer">
      <button class="confirm-btn" :disabled="totalQuantity === 0" @click="goToCreateOrder">
        确认预订（{{ totalQuantity }}人）
      </button>
    </div>
  </div>
</template>

<script>
import { getSkuPrices } from '@/api/browse'

export default {
  name: 'ProductCalendar',
  data() {
    const now = new Date()
    return {
      productId: '',
      skuId: '',
      selectedDateStr: '',
      selectedCalendarId: null,
      priceItems: [],
      calYear: now.getFullYear(),
      calMonth: now.getMonth(),
      calPriceMap: {}
    }
  },
  computed: {
    // 金额一律按整数分累加，避免浮点乘加误差；仅用于展示时才转换回元
    totalPriceCents() {
      return this.priceItems.reduce((s, i) => s + Math.round(i.price * 100) * (i.quantity || 0), 0)
    },
    totalPriceYuan() {
      return (this.totalPriceCents / 100).toFixed(2)
    },
    totalQuantity() {
      return this.priceItems.reduce((s, i) => s + (i.quantity || 0), 0)
    },
    calDays() {
      const year = this.calYear
      const month = this.calMonth
      const first = new Date(year, month, 1).getDay()
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const days = []
      for (let i = 0; i < first; i++) {
        const prevDate = new Date(year, month, -first + i + 1)
        days.push({ date: prevDate.getDate(), current: false, disabled: true, dateStr: '', hasPrice: false })
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const dateObj = new Date(year, month, d)
        dateObj.setHours(0, 0, 0, 0)
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
        const priceInfo = this.calPriceMap[dateStr]
        days.push({
          date: d,
          current: true,
          disabled: dateObj < today,
          dateStr,
          hasPrice: !!priceInfo,
          minPrice: priceInfo ? priceInfo.minPrice : null
        })
      }
      return days
    }
  },
  mounted() {
    this.productId = this.$route.params.id
    this.skuId = this.$route.query.skuId
    if (this.skuId) this.loadSkuPrices()
  },
  methods: {
    async loadSkuPrices() {
      try {
        const month = `${this.calYear}-${String(this.calMonth + 1).padStart(2, '0')}`
        const res = await getSkuPrices({ skuId: this.skuId, month })
        const map = {}
        ;(res.data || []).forEach(cp => {
          const dateStr = (cp.calendar?.priceDate || cp.priceDate || '').substring(0, 10)
          if (dateStr) {
            const prices = cp.priceItems || []
            const minPrice = prices.reduce((min, p) => Math.min(min, p.price), Infinity)
            map[dateStr] = { minPrice, priceItems: prices, calendarId: cp.calendar?.id || cp.id }
          }
        })
        this.calPriceMap = map
      } catch (e) {
        console.error('加载价格失败:', e)
      }
    },
    prevMonth() {
      if (this.calMonth === 0) { this.calYear--; this.calMonth = 11 }
      else this.calMonth--
      this.selectedDateStr = ''
      this.priceItems = []
      this.loadSkuPrices()
    },
    nextMonth() {
      if (this.calMonth === 11) { this.calYear++; this.calMonth = 0 }
      else this.calMonth++
      this.selectedDateStr = ''
      this.priceItems = []
      this.loadSkuPrices()
    },
    selectCalDate(day) {
      if (!day.current || day.disabled || !day.hasPrice) return
      this.selectedDateStr = day.dateStr
      const info = this.calPriceMap[day.dateStr]
      this.selectedCalendarId = info.calendarId
      this.priceItems = info.priceItems.map(p => ({ ...p, quantity: 0 }))
      if (this.priceItems.length) this.priceItems[0].quantity = 1
    },
    changeQty(item, delta) {
      const n = (item.quantity || 0) + delta
      if (n < 0) return
      this.$set(item, 'quantity', n)
    },
    goToCreateOrder() {
      if (!this.skuId || !this.selectedDateStr || this.totalQuantity === 0) return
      const selectedItems = this.priceItems.filter(i => i.quantity > 0)
      this.$router.push({
        name: 'OrderCreate',
        params: { id: this.productId },
        query: {
          productId: this.productId,
          skuId: this.skuId,
          calendarPriceId: this.selectedCalendarId,
          date: this.selectedDateStr,
          priceItems: encodeURIComponent(JSON.stringify(selectedItems.map(i => ({
            priceItemId: i.id,
            priceName: i.priceName,
            price: i.price,
            quantity: i.quantity
          }))))
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.calendar-page {
  width: 100%;
  min-height: 100vh;
  background: #f9fafb;
  padding-bottom: 80px;
}

.page-header {
  display: flex;
  align-items: center;
  background: #fff;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 20;
}

.back-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #ff720d;
  cursor: pointer;
  margin-right: 12px;
  padding: 0;
}

.page-title { font-size: 16px; font-weight: 600; color: #111827; }

.calendar-wrap { background: #fff; margin: 12px; border-radius: 12px; padding: 16px; }

.calendar-month-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.month-btn {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  width: 32px;
  height: 32px;
  font-size: 18px;
  cursor: pointer;
  color: #374151;
}
.month-label { font-size: 15px; font-weight: 600; color: #111827; }

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  margin-bottom: 4px;
}
.weekday { text-align: center; font-size: 12px; color: #6b7280; padding: 4px 0; }

.calendar-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.cal-day {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  &.other-month { opacity: 0.3; cursor: default; }
  &.disabled { color: #d1d5db; cursor: not-allowed; background: transparent; }
  &.has-price { background: #d1fae5; }
  &.selected { background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%); color: #fff; .cal-day-price { color: #ffe0bf; } }
  &:not(.disabled):not(.other-month):active { transform: scale(0.95); }
}
.cal-day-num { font-size: 13px; font-weight: 500; }
.cal-day-price { font-size: 10px; color: #059669; }

.calendar-tip { font-size: 12px; color: #9ca3af; text-align: center; margin-top: 8px; }

.price-detail { background: #fff; margin: 12px; border-radius: 12px; padding: 16px; }
.section-title { font-size: 15px; font-weight: 600; color: #111827; margin-bottom: 12px; }

.price-list { display: flex; flex-direction: column; gap: 12px; }
.price-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.price-name { font-size: 14px; color: #111827; margin-bottom: 4px; }
.price-val { font-size: 13px; color: #ff720d; font-weight: 600; }

.qty-ctrl { display: flex; align-items: center; gap: 10px; }
.qty-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #e5e7eb;
  border-radius: 50%;
  background: #fff;
  font-size: 16px;
  color: #ff720d;
  cursor: pointer;
  &:active { background: #f3f4f6; }
}
.qty-num { font-size: 14px; font-weight: 600; color: #111827; min-width: 20px; text-align: center; }

.price-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #f0f0f0;
}
.total-label { font-size: 14px; color: #6b7280; }
.total-val { font-size: 20px; font-weight: 700; color: #ff720d; }

.calendar-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  z-index: 10;
}

.confirm-btn {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  color: #fff;
  transition: opacity 0.2s;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):active { opacity: 0.85; }
}
</style>
